import { useMemo, useState } from 'react';
import { Download, BarChart2 } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Skeleton,
  SegmentedControl,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Chart } from '@vnetwork/ui/charts';
import { useToast } from '@vnetwork/ui/feedback';
import { hasSeriesData } from '../../_shared/AnalyticsKit';
import {
  useVodDurationUsage,
  useVodVolumeUsage,
  fetchVodUsageCsv,
} from '@/api/media/vod-usage';
import type {
  VodDurationUsage,
  VodVolumeUsage,
} from '@/api/media/vod-usage-types';
import { VodConsoleBreadcrumbs } from '../VodConsoleBreadcrumbs';
import styles from './UsagePage.module.css';

// ─── Chart group definitions (mirrors legacy SERIES_GROUPS) ──────────────────

interface SeriesKey {
  key: keyof VodDurationUsage;
  label: string;
}

interface ChartGroupDef {
  title: string;
  subtitle: string;
  series: SeriesKey[];
}

const CHART_GROUPS: Record<string, ChartGroupDef> = {
  overview: {
    title: 'Overview',
    subtitle: 'Total transcoding and transmux activity.',
    series: [
      { key: 'total', label: 'Total' },
      { key: 'transmux', label: 'Transmux' },
      { key: 'sdTranscoding', label: 'SD Transcoding' },
      { key: 'hdTranscoding', label: 'HD Transcoding' },
      { key: 'uhdTranscoding', label: 'UHD Transcoding' },
    ],
  },
  transmux: {
    title: 'Transmux',
    subtitle: 'Transmux by resolution tier.',
    series: [
      { key: 'sdTransmux', label: 'SD' },
      { key: 'hdTransmux', label: 'HD' },
      { key: 'uhdTransmux', label: 'UHD' },
    ],
  },
  cpuTranscoding: {
    title: 'CPU Transcoding',
    subtitle: 'CPU-based transcoding broken out by resolution.',
    series: [
      { key: 'sdTranscoding', label: 'SD' },
      { key: 'hdTranscoding', label: 'HD' },
      { key: 'uhdTranscoding', label: 'UHD' },
      { key: 'audioOnly', label: 'Audio Only' },
    ],
  },
  h264: {
    title: 'H.264',
    subtitle: 'CPU H.264 transcoding by resolution.',
    series: [
      { key: 'h264SdTranscoding', label: 'SD' },
      { key: 'h264HdTranscoding', label: 'HD' },
      { key: 'h264UhdTranscoding', label: 'UHD' },
    ],
  },
  h265: {
    title: 'H.265',
    subtitle: 'CPU H.265 transcoding by resolution.',
    series: [
      { key: 'h265SdTranscoding', label: 'SD' },
      { key: 'h265HdTranscoding', label: 'HD' },
      { key: 'h265UhdTranscoding', label: 'UHD' },
    ],
  },
};

const GROUP_ORDER = ['overview', 'transmux', 'cpuTranscoding', 'h264', 'h265'] as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────

type UsageRow = VodDurationUsage | VodVolumeUsage;

function buildChartData(
  rows: UsageRow[],
  keys: SeriesKey[],
): Array<Record<string, string | number>> {
  return rows.map((row) => {
    const r = row as unknown as Record<string, string>;
    const point: Record<string, string | number> = { month: r.month };
    for (const { key } of keys) {
      point[key as string] = parseFloat(r[key as string] ?? '0') || 0;
    }
    return point;
  });
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Single chart card ────────────────────────────────────────────────────────

type TabKey = 'duration' | 'volume';

function UsageChartCard({
  group,
  durationData,
  volumeData,
  durationLoading,
  volumeLoading,
  durationYear,
  volumeYear,
  yearOptions,
  onDurationYearChange,
  onVolumeYearChange,
  onDownload,
  downloadingType,
}: {
  group: ChartGroupDef;
  durationData: Array<Record<string, string | number>>;
  volumeData: Array<Record<string, string | number>>;
  durationLoading: boolean;
  volumeLoading: boolean;
  durationYear: number;
  volumeYear: number;
  yearOptions: number[];
  onDurationYearChange: (y: number) => void;
  onVolumeYearChange: (y: number) => void;
  onDownload: (type: 'duration' | 'volume', year: number) => void;
  downloadingType: 'duration' | 'volume' | null;
}) {
  const [tab, setTab] = useState<TabKey>('duration');
  const isLoading = tab === 'duration' ? durationLoading : volumeLoading;
  const year = tab === 'duration' ? durationYear : volumeYear;
  const data = tab === 'duration' ? durationData : volumeData;
  const unit = tab === 'duration' ? 'Hour(s)' : 'GB';

  const chartSeries = group.series.map(({ key, label }) => ({
    key: key as string,
    label,
  }));

  return (
    <Card flat>
      <CardHeader
        actions={
          <div className={styles.chartActions}>
            <SegmentedControl
              aria-label="Usage metric"
              value={tab}
              onValueChange={(v) => setTab(v as TabKey)}
              items={[
                { value: 'duration', label: 'Duration' },
                { value: 'volume', label: 'Volume' },
              ]}
            />
            <Select
              value={String(year)}
              onValueChange={(v) =>
                tab === 'duration' ? onDurationYearChange(Number(v)) : onVolumeYearChange(Number(v))
              }
            >
              <SelectTrigger size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="secondary"
              startIcon={<Download size={13} />}
              disabled={downloadingType !== null}
              onClick={() => onDownload(tab, year)}
            >
              {downloadingType === tab ? 'Downloading…' : 'CSV'}
            </Button>
          </div>
        }
      >
        <CardTitle>{group.title}</CardTitle>
        <CardDescription>{group.subtitle}</CardDescription>
      </CardHeader>
      <CardBody>
        {isLoading ? (
          <Skeleton variant="rect" height={240} />
        ) : !hasSeriesData(data, 'month') ? (
          <EmptyState icon={<BarChart2 size={20} />} size="sm" title={`No data for ${year}`} description="No usage data is available for the selected year." />
        ) : (
          <Chart
            type="bar"
            data={data}
            xKey="month"
            series={chartSeries}
            height={240}
            stacked
            // Tooltip entries (key present) carry the unit; bare y-axis ticks stay numeric.
            valueFormatter={(v, key) => (key ? `${v} ${unit}` : v)}
          />
        )}
      </CardBody>
    </Card>
  );
}

// ─── Main usage page ──────────────────────────────────────────────────────────

export function VodUsage({ serviceId }: { serviceId: string }) {
  const toast = useToast();

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1];

  const [durationYear, setDurationYear] = useState(currentYear);
  const [volumeYear, setVolumeYear] = useState(currentYear);
  const [downloadingType, setDownloadingType] = useState<'duration' | 'volume' | null>(null);

  const { data: durationRaw = [], isLoading: durationLoading } = useVodDurationUsage(
    serviceId,
    durationYear,
  );
  const { data: volumeRaw = [], isLoading: volumeLoading } = useVodVolumeUsage(
    serviceId,
    volumeYear,
  );

  const handleDownload = async (type: 'duration' | 'volume', year: number) => {
    setDownloadingType(type);
    try {
      const blob = await fetchVodUsageCsv(serviceId, type, year);
      triggerDownload(blob, `${type}-usage-${year}.csv`);
    } catch (err) {
      toast.error((err as Error).message || `Failed to download ${type} CSV`);
    } finally {
      setDownloadingType(null);
    }
  };

  const chartCards = useMemo(
    () =>
      GROUP_ORDER.map((groupKey) => {
        const group = CHART_GROUPS[groupKey];
        return {
          key: groupKey,
          group,
          durationData: buildChartData(durationRaw, group.series),
          volumeData: buildChartData(volumeRaw, group.series),
        };
      }),
    [durationRaw, volumeRaw],
  );

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<VodConsoleBreadcrumbs trail={[{ label: 'Usage' }]} />}
        title="Usage"
        description="Monthly transcoding duration and volume breakdown by encoding type."
      />
      <div className={styles.chartGrid}>
        {chartCards.map(({ key, group, durationData, volumeData }) => (
          <UsageChartCard
            key={key}
            group={group}
            durationData={durationData}
            volumeData={volumeData}
            durationLoading={durationLoading}
            volumeLoading={volumeLoading}
            durationYear={durationYear}
            volumeYear={volumeYear}
            yearOptions={yearOptions}
            onDurationYearChange={setDurationYear}
            onVolumeYearChange={setVolumeYear}
            onDownload={handleDownload}
            downloadingType={downloadingType}
          />
        ))}
      </div>
    </Container>
  );
}
